import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { Table, Input, message, Tag, Select, Form, Button, Space, Tooltip, Modal, DatePicker, Card } from 'antd';
import { EyeOutlined, PlayCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { Helmet } from "react-helmet";
import startCase from "lodash/startCase";
import { Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PreviewMedia from '../components/Library/PreviewMedia';
import PreviewPlaylist from '../components/Playlists/PreviewPlaylist';
import { getMediaIcon } from '../utils/Misc';
import Link from "antd/es/typography/Link";
import RequirePermission from "../components/Common/RequirePermission";
import ClearFiltersButton from "../components/Common/ClearFiltersButton";

dayjs.extend(utc);
dayjs.extend(timezone);

// Matches a MySQL DATETIME ("YYYY-MM-DD HH:mm:ss") or an ISO 8601 timestamp
// (with an optional fractional-seconds/timezone suffix) - deliberately
// narrow so a diff on an unrelated numeric/string field (an id, a status
// code, etc.) is never mistaken for a date and reformatted.
const DATE_LIKE_PATTERN = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

const formatIfDate = (val) => {
  if (typeof val !== "string" || !DATE_LIKE_PATTERN.test(val)) return null;
  const parsed = dayjs(val).tz("Asia/Kolkata");
  return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm:ss") : null;
};

const { Title } = Typography;
const Logs = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);
    const [filterText, setFilterText] = useState("");
    const [viewPermission, setViewPermission] = useState(false);
    const [user, setUser] = useState(null);
    const [logs, setLogs] = useState([]);
    const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
    const [selectedModule, setSelectedModule] = useState("");
    const [selectedOperation, setSelectedOperation] = useState("");
    const [selectedDateRange, setSelectedDateRange] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [title, setTitle] = useState("");
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isPlaylistPreviewModalOpen, setIsPlaylistPreviewModalOpen] = useState(false);
    const [previewMedia, setPreviewMedia] = useState(null);
    const [previewPlaylist, setPreviewPlaylist] = useState(null);

    const operationOptions = [
        {
            value: "insert",
            label: "Insert",
        },
        {
            value: "update",
            label: "Update",
        },
        {
            value: "delete",
            label: "Delete",
        },
        {
            value: "reject",
            label: "Reject",
        },
        {
            value: "approve",
            label: "Approve",
        },
        {
            value: "merge",
            label: "Merge",
        },
        {
            value: "restore",
            label: "Restore",
        },
        {
            value: "login",
            label: "Login",
        },
        {
            value: "signup",
            label: "Signup",
        }
    ];

    // One distinct color per real operation type, so delete/reject/approve/
    // merge/restore are no longer all indistinguishable red Tags.
    const operationColors = {
        insert: "green",
        update: "blue",
        delete: "red",
        approve: "cyan",
        reject: "orange",
        merge: "purple",
        restore: "gold",
        login: "lime",
        signup: "geekblue",
    };

    // Every table_name actually written to audit_logs (see log.service.js's
    // own TABLE_LABELS on the backend) - previously only 7 of the ~18 real
    // tables were listed here, so activity on the rest (campaigns, cron
    // emails, schedule sequences, etc.) could never be filtered to.
    const moduleNameOptions = [
        { value: "users", label: "Users" },
        { value: "screens", label: "Screens" },
        { value: "playlists", label: "Playlists" },
        { value: "media", label: "Media" },
        { value: "roles", label: "Roles" },
        { value: "schedules", label: "Schedules" },
        { value: "devices", label: "Devices" },
        { value: "campaigns", label: "Campaigns" },
        { value: "android_app_versions", label: "Android App Versions" },
        { value: "cron_emails", label: "Scheduled Emails" },
        { value: "approver_lists", label: "Approver Lists" },
        { value: "schedule_screens", label: "Schedule Screen Assignments" },
        { value: "schedule_sequences", label: "Schedule Sequences" },
        { value: "sequence_playlist_times", label: "Sequence Playlist Times" },
        { value: "sequence_dates", label: "Sequence Dates" },
        { value: "location_users", label: "Location Assignments" },
        { value: "campaign_days", label: "Campaign Days" },
        { value: "campaign_locations", label: "Campaign Locations" },
    ];

    const setQueryParams = (params) => {
        const searchParams = new URLSearchParams(params);
        navigate(`?${searchParams.toString()}`, { replace: true });
    };
    const handleTableChange = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        const newQueryParams = {
            filter: value,
            limit: pageSize,
            module: selectedModule,
            operation: selectedOperation,
            page: 1,
        };
        setFilterText(value);
        setQueryParams(newQueryParams);
        setCurrentPage(1);
    };

    const handleModuleChange = (value) => {
        setSelectedModule(value);
        const newQueryParams = {
            filter: filterText,
            module: value,
            operation: selectedOperation,
            limit: pageSize,
            page: 1,
        };
        setQueryParams(newQueryParams);
        setCurrentPage(1);
    };

    const handleOperationChange = (value) => {
        setSelectedOperation(value);
        const newQueryParams = {
            filter: filterText,
            operation: value,
            module: selectedModule,
            limit: pageSize,
            page: 1,
        };
        setQueryParams(newQueryParams);
        setCurrentPage(1);
    };

    const handleDateRangeChange = (dates) => {
        setSelectedDateRange(dates || []);
        const newQueryParams = {
            filter: filterText,
            module: selectedModule,
            operation: selectedOperation,
            limit: pageSize,
            page: 1,
        };
        setQueryParams(newQueryParams);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilterText("");
        setSelectedModule("");
        setSelectedOperation("");
        setSelectedDateRange([]);
        setCurrentPage(1);
        setQueryParams({
            filter: "",
            module: "",
            operation: "",
            limit: pageSize,
            page: 1,
        });
    };

    const handleView = async (record) => {
        try {
            setLoading(true);
            const response = await axios.get(
                process.env.REACT_APP_BASE_URL + `/api/logs/${record.id}`
            );
            setLogs(response.data.data.results);
            setTitle(response.data.data.results[0].title);
            setIsLogsModalOpen(true);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            message.error("Error fetching logs data");
            console.log("Error fetching logs:", error);
        }
    };

    const handlePreviewMedia = (mediaData) => {
        setIsPreviewModalOpen(true);
        setPreviewMedia(mediaData);
        console.log(mediaData);
    };

    const handlePreviewPlaylist = (record) => {
        setIsPlaylistPreviewModalOpen(true);
        setPreviewPlaylist({ ...record, media: JSON.parse(record.media_details) });
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {
                filter: filterText,
                limit: pageSize,
                page: currentPage,
                module: selectedModule,
                operation: selectedOperation,
            };
            if (selectedDateRange.length === 2) {
                params.start_date = selectedDateRange[0].format("YYYY-MM-DD");
                params.end_date = selectedDateRange[1].format("YYYY-MM-DD");
            }
            if (selectedDateRange.length === 0) {
                params.start_date = "";
                params.end_date = "";
            }
            const response = await axios.get(
                process.env.REACT_APP_BASE_URL + `/api/logs`,
                { params }
            );
            let records = response.data.data.data;
            setTotalRecords(response.data.data.total);
            setDetail(records);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Error fetching logs data", error);
        }
    };

    useEffect(() => {
        const fetchTimer = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(fetchTimer);
    }, [filterText, pageSize, currentPage, selectedModule, selectedOperation, selectedDateRange]);

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("userData"));
        setUser(storedUser);
        const role_id = storedUser.role_id;
        setViewPermission(role_id === 2);
    }, []);

    const columns = [
        {
            title: "Activity",
            dataIndex: "summary",
            key: "summary",
            render: (text, record) => (
                <span>{text || `${startCase(record.action)} on ${startCase(record.table_name)}`}</span>
            )
        },
        {
            title: "Module",
            dataIndex: "module_label",
            key: "module",
            render: (text, record) => (
                <span>{text || startCase(record.table_name)}</span>
            )
        },
        {
            title: "Operation Type",
            dataIndex: "action",
            key: "operation_type",
            render: (text) => (
                <Tag color={operationColors[text] || "default"}>
                    {startCase(text)}
                </Tag>
            )
        },
        {
            title: "Created By",
            dataIndex: "created_by",
            key: "created_by",
            render: (text) => (
                <span>{text}</span>
            )
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (text) => (
                <span className="text-small">{dayjs(text).format("YYYY-MM-DD HH:mm")}</span>
            )
        },
        {
            title: "Action",
            render: (_, record) => (
                <Space>
                    {user && (
                        <>
                            {viewPermission && (
                                <Tooltip title="View">
                                    <Button

                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => {
                                            handleView(record);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </>
                    )}
                </Space>
            ),
        }
    ];

    // Bookkeeping columns that change on virtually every write but never
    // mean anything to a reader - always filtered out of the readable diff
    // below so the list stays focused on fields an admin actually changed.
    const NOISE_FIELDS = new Set(["updated_at", "modified_at", "created_at", "id"]);

    // A nested field (e.g. a role's `permissions`) isn't always stored the
    // same way on both sides of a diff - sometimes it's a real nested object
    // by the time old_data/new_data's outer JSON is parsed, sometimes it's
    // still its own JSON *string* one level down (double-encoded). Both need
    // to end up as an actual object before rendering, or a still-encoded
    // side prints as raw JSON text while the already-decoded side prints as
    // the literal string "[object Object]".
    const parseMaybeJSON = (val) => {
        if (typeof val !== "string") return val;
        const trimmed = val.trim();
        if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return val;
        try {
            return JSON.parse(trimmed);
        } catch (e) {
            return val;
        }
    };

    const isPlainObject = (val) => val !== null && typeof val === "object" && !Array.isArray(val);

    const formatDiffValue = (val) => {
        if (val === null || val === undefined || val === "") return "empty";
        const asDate = formatIfDate(val);
        if (asDate) return asDate;
        return String(val);
    };

    const cellStyle = { padding: "4px 8px", border: "1px solid #f0f0f0", textAlign: "left" };

    // Renders a changed object-valued field (permissions, settings blobs,
    // etc.) as a small table of just the sub-keys that actually differ,
    // rather than dumping the entire object on one line - a role with 80
    // permission keys only needs the 2 that were toggled shown, not all 80.
    const renderObjectDiff = (oldObj, newObj) => {
        const subKeys = Array.from(
            new Set([...Object.keys(oldObj), ...Object.keys(newObj)])
        ).filter((k) => formatDiffValue(oldObj[k]) !== formatDiffValue(newObj[k]));

        if (subKeys.length === 0) return null;

        return (
            <table style={{ borderCollapse: "collapse", marginTop: 6, width: "100%", maxWidth: 480 }}>
                <thead>
                    <tr>
                        <th style={cellStyle}>Field</th>
                        <th style={cellStyle}>Old</th>
                        <th style={cellStyle}>New</th>
                    </tr>
                </thead>
                <tbody>
                    {subKeys.map((k) => (
                        <tr key={k}>
                            <td style={cellStyle}>{startCase(k)}</td>
                            <td style={{ ...cellStyle, color: "#888" }}>{formatDiffValue(oldObj[k])}</td>
                            <td style={cellStyle}><strong>{formatDiffValue(newObj[k])}</strong></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderDataComparison = (oldData, newData) => {
        const oldObj = typeof oldData === "string" ? JSON.parse(oldData || "{}") : oldData || {};
        const newObj = typeof newData === "string" ? JSON.parse(newData || "{}") : newData || {};

        const allKeys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)]));
        const changes = allKeys
            .filter((key) => !NOISE_FIELDS.has(key))
            .map((key) => ({
                key,
                oldVal: parseMaybeJSON(oldObj[key]),
                newVal: parseMaybeJSON(newObj[key]),
            }))
            .filter(({ oldVal, newVal }) => JSON.stringify(oldVal) !== JSON.stringify(newVal));

        if (changes.length === 0) {
            return (
                <div style={{ color: "#888", fontStyle: "italic", padding: "8px" }}>
                    No field changes recorded for this entry.
                </div>
            );
        }

        return (
            <ul style={{ margin: 0, padding: "8px 8px 8px 24px" }}>
                {changes.map(({ key, oldVal, newVal }) => {
                    if (isPlainObject(oldVal) || isPlainObject(newVal)) {
                        const oldSub = isPlainObject(oldVal) ? oldVal : {};
                        const newSub = isPlainObject(newVal) ? newVal : {};
                        return (
                            <li key={key} style={{ marginBottom: 10 }}>
                                <strong>{startCase(key)}</strong> changed:
                                {renderObjectDiff(oldSub, newSub)}
                            </li>
                        );
                    }
                    return (
                        <li key={key} style={{ marginBottom: 6 }}>
                            <strong>{startCase(key)}</strong> changed from{" "}
                            <span style={{ color: "#888" }}>{formatDiffValue(oldVal)}</span> to{" "}
                            <strong>{formatDiffValue(newVal)}</strong>
                        </li>
                    );
                })}
            </ul>
        );
    };

    const columnsLogs = [
        {
            title: `${title ?? "No Title"}`,
            key: "dataComparison",
            render: (_, record) => (
                <>
                    <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>
                            <Tag color={operationColors[record.action] || "default"}>{startCase(record.action)}</Tag>
                            {record.summary}
                        </span>
                        {(record.table_name === "media") && (<Link href="#" onClick={() => handlePreviewMedia(record.media)}>
                            <Tag>{getMediaIcon(record.media.media_type)} Preview</Tag>
                        </Link>)}
                        {(record.table_name === "playlists") && (<Link href="#" onClick={() => handlePreviewPlaylist(record.playlist)}>
                            <Tag color='green'><PlayCircleOutlined style={{ marginRight: "5px" }}/> Preview </Tag>
                        </Link>)}
                    </div>
                    <Card size="small" style={{ background: "#fafafa", borderRadius: 8 }}>
                        {renderDataComparison(record.old_data, record.new_data)}
                    </Card>
                </>
            ),
        },
    ];

    return (
        <RequirePermission permission="signage.logs_view">
        <div style={{ padding: "1rem" }}>
            <Helmet>
                <title>{`${process.env.REACT_APP_NAME} | Logs`}</title>
            </Helmet>
            <Title level={3} style={{ marginBottom: "1rem" }}>
                Logs
            </Title>
            <div className="d-sm-block d-md-flex justify-content-between mb-3">
                <Form layout="inline">
                    <Form.Item>
                        <Input.Search
                            placeholder="Search logs"
                            value={filterText}
                            className="me-1"
                            onChange={handleSearch}
                            style={{ width: 200 }}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            icon={<FilterOutlined />}
                            type={showFilters ? "primary" : "default"}
                            onClick={() => setShowFilters((prev) => !prev)}
                        >
                            Filters
                            {(selectedModule || selectedOperation || selectedDateRange.length === 2) && (
                                <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>
                                    {[selectedModule, selectedOperation, selectedDateRange.length === 2 ? "date" : ""].filter(Boolean).length}
                                </Tag>
                            )}
                        </Button>
                    </Form.Item>
                </Form>
            </div>

            {showFilters && (
                <Form layout="inline" className="mb-3">
                    <Form.Item label="Module">
                        <Select
                            value={selectedModule}
                            allowClear
                            onChange={handleModuleChange}
                            style={{ width: 200 }}
                            showSearch
                            filterOption={(input, option) => {
                                return (option?.children ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase());
                            }}
                        >
                            {moduleNameOptions.map((module) => (
                                <Select.Option key={module.value} value={module.value}>
                                    {module.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Operation">
                        <Select
                            value={selectedOperation}
                            allowClear
                            onChange={handleOperationChange}
                            style={{ width: 200 }}
                            showSearch
                            filterOption={(input, option) => {
                                return (option?.children ?? "")
                                    .toLowerCase()
                                    .includes(input.toLowerCase());
                            }}
                        >
                            {operationOptions.map((opr) => (
                                <Select.Option key={opr.value} value={opr.value}>
                                    {startCase(opr.label)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Date">
                        <DatePicker.RangePicker
                            value={selectedDateRange}
                            allowClear
                            onChange={handleDateRangeChange}
                            onCancel={() => {
                                setSelectedDateRange([]);
                            }}
                            style={{ width: 250 }}
                        />
                    </Form.Item>
                    <Form.Item style={{ marginLeft: "auto", alignSelf: "flex-end" }}>
                        <ClearFiltersButton onClick={handleClearFilters} />
                    </Form.Item>
                </Form>
            )}
            <Table
                loading={loading}
                columns={columns}
                dataSource={detail}
                scroll={{ x: "max-content" }}
                rowKey={(record) => record.id}
                onChange={handleTableChange}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalRecords,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} locations`,
                    showSizeChanger: true, // Enables the page size selection dropdown
                    pageSizeOptions: ["5", "10", "20", "50", "100"], // Options for the page size selection
                }}
            />
            {isLogsModalOpen && (
                <Modal
                    title="Log Details"
                    open={isLogsModalOpen}
                    onCancel={() => setIsLogsModalOpen(false)}
                    footer={null}
                    width={800}
                >
                    <Table
                        loading={loading}
                        columns={columnsLogs}
                        dataSource={logs}
                        scroll={{ x: "max-content" }}
                        rowKey={(record) => record.id}
                        pagination={false}
                    />
                </Modal>
            )}
            {isPreviewModalOpen && (
                <PreviewMedia
                    isModalOpen={isPreviewModalOpen}
                    closeModal={() => setIsPreviewModalOpen(false)}
                    media={previewMedia}
                />
            )}
            {isPlaylistPreviewModalOpen && (
                <PreviewPlaylist
                    isModalOpen={isPlaylistPreviewModalOpen}
                    closeModal={() => setIsPlaylistPreviewModalOpen(false)}
                    playlist={previewPlaylist}
                />
            )}
        </div>
        </RequirePermission>
    )
}

export default Logs;